import { isUserHasKey } from '../../GlobalFunctions';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import { checkEmailExists } from '../../BackendInterface';
import styles from './AddStudents.module.scss';

interface FormValues {
  [key: string]: string;
}

interface Props {
  values: FormValues;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
  validationAttemptCount: number;
  onValidationAttempt: () => void;
  studentNames: string[];
  handleRemoveStudent: (arg0: number) => void;
  handleAddStudent: (arg0: FormValues, arg1: (field: string, value: any, shouldValidate?: boolean) => void) => void;
  setFieldError: (field: string, errorMsg: string) => void;
}

const EMAIL_ALREADY_EXISTS_ERROR = 'This email is already associated with an existing login.';
const EMAIL_CHECK_FAILED_ERROR = "We couldn't verify this email right now. Please try again.";

const AddSingleStudent = ({
  values,
  setFieldValue,
  setFieldTouched,
  validationAttemptCount,
  onValidationAttempt,
  studentNames,
  handleRemoveStudent,
  handleAddStudent,
  setFieldError,
}: Props): JSX.Element => {
  const preValidateOnAdd = async () => {
    const { studentName, password, email } = values;
    let hasErrors = false;

    // Increment validation attempt counter FIRST (before any async operations)
    onValidationAttempt();

    // Mark fields as touched to enable error display
    setFieldTouched('studentName', true, false);
    setFieldTouched('password', true, false);
    setFieldTouched('email', true, false);

    // Manually validate required fields first (catch empty fields)
    if (!studentName || studentName.trim() === '') {
      setFieldError('studentName', 'Student name is required');
      hasErrors = true;
    } else if (studentName.trim().length < 4) {
      setFieldError('studentName', 'Please enter a student name (min 4 characters)');
      hasErrors = true;
    }

    if (!password || password.trim() === '') {
      setFieldError('password', 'Password is required');
      hasErrors = true;
    } else if (password.trim().length < 6) {
      setFieldError('password', 'Minimum 6 characters');
      hasErrors = true;
    }

    // Validate email format if provided
    if (email && email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setFieldError('email', 'Please enter a valid email address');
        hasErrors = true;
      } else {
        try {
          const exists = await checkEmailExists(email.trim());
          if (exists) {
            setFieldError('email', EMAIL_ALREADY_EXISTS_ERROR);
            hasErrors = true;
          }
        } catch {
          setFieldError('email', EMAIL_CHECK_FAILED_ERROR);
          hasErrors = true;
        }
      }
    }

    if (!hasErrors) {
      // No errors - add to queue
      handleAddStudent(values, setFieldValue);
    }
  };
  return (
    <>
      <TextInput
        label='Student Name'
        name='studentName'
        type='text'
        placeholder=''
        required
        validationAttemptCount={validationAttemptCount}
      />
      <TextInput
        label='Student Password'
        name='password'
        type='text'
        disabled={isUserHasKey('KEY_MANAGE_STUDENTS') === null ? true : !isUserHasKey('KEY_MANAGE_STUDENTS')}
        required
        validationAttemptCount={validationAttemptCount}
      />
      <TextInput
        label='School Email Address'
        name='email'
        type='email'
        placeholder=''
        validationAttemptCount={validationAttemptCount}
      />
      <div className={styles.buttonRow}>
        <p className={styles.noteForAddingSingleStudent}>
          Please note student passwords cannot be changed once added to your class.
        </p>
        <Button type='button' small onClick={preValidateOnAdd}>
          ADD STUDENT
        </Button>
      </div>
      <h4 className={styles.studentBoxTitle}>Students To Add</h4>
      <div className={styles.studentBox}>
        {studentNames.length === 0 ? (
          <p className={styles.helpText}>Add a Student...</p>
        ) : (
          studentNames.map((name, index) => {
            return (
              <div key={`${name} ${index}`} className={styles.addedStudent}>
                {name}
                <button type='button' onClick={() => handleRemoveStudent(index)}>
                  x
                </button>
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

export default AddSingleStudent;
